/**
 * RadarFilterDrawer — Konzept 3 inline filter panel (filter-fix-v2).
 *
 * The drawer is the FINAL layer: no nested popovers. All dimensions render
 * inline. High-cardinality dimensions use FilterChecklistSection (search +
 * scrollable list). Respects the delegation lock (read-only when locked).
 */
import { useEffect, useRef } from 'react'
import { X, RotateCcw, Lock } from 'lucide-react'
import type { RadarBubble, RadarFilterState, RadarType } from '@/services/api/teacher'
import { FilterChecklistSection, type ChecklistOption } from './FilterChecklistSection'
import {
  STATUS_BUCKET_META,
  ZONE_META,
  TYPE_META,
} from '../types/filter'
import {
  bucketForBubble,
  zoneForBubble,
  deriveOptionsWithCounts,
} from '../utils/filterBubbles'
import type { RadarI18n } from '../i18n'

interface Props {
  open: boolean
  onClose: () => void
  filterState: RadarFilterState
  setFilter: (partial: Partial<RadarFilterState>) => void
  resetFilter: () => void
  isLocked: boolean
  radarType: RadarType
  showIncoming: boolean
  /** Unfiltered bubbles — used to derive option lists & live counts. */
  allBubbles: RadarBubble[]
  totalCount?: number
  visibleCount?: number
  t: RadarI18n
  directorEnabled?: boolean
}

export function RadarFilterDrawer({
  open,
  onClose,
  filterState,
  setFilter,
  resetFilter,
  isLocked,
  radarType,
  showIncoming,
  allBubbles,
  totalCount,
  visibleCount,
  t,
  directorEnabled = false,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Escape to close + focus trap entry
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    panelRef.current?.focus()
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  if (!open) return null

  const ft = t.filter
  const isAdmin = radarType === 'admin'
  const disabled = isLocked

  // ---- Counts & options derived from unfiltered bubbles ----
  const statusCounts: Record<string, number> = {}
  const zoneCounts: Record<string, number> = {}
  for (const b of allBubbles) {
    const sb = bucketForBubble(b)
    statusCounts[sb] = (statusCounts[sb] ?? 0) + 1
    const z = zoneForBubble(b)
    zoneCounts[z] = (zoneCounts[z] ?? 0) + 1
  }

  const subjectOptions: ChecklistOption[] = deriveOptionsWithCounts(allBubbles, 'subject_name').map((o) => ({
    value: o.value, label: o.value, count: o.count,
  }))
  const facultyOptions: ChecklistOption[] = deriveOptionsWithCounts(allBubbles, 'requester_faculty').map((o) => ({
    value: o.value, label: o.value, count: o.count,
  }))
  const classOptions: ChecklistOption[] = deriveOptionsWithCounts(allBubbles, 'klass_name').map((o) => ({
    value: o.value, label: o.value, count: o.count,
  }))
  const requesterOptions: ChecklistOption[] = deriveOptionsWithCounts(allBubbles, 'requester_short_code').map((o) => ({
    value: o.value, label: o.value, count: o.count,
  }))

  const toggleInList = (key: 'status' | 'zones' | 'types', value: string) => {
    const current = filterState[key] as string[]
    if (key === 'status' && filterState.activeOnly) {
      const next = current.includes(value) ? current : [...current, value]
      setFilter({ status: next, activeOnly: false } as Partial<RadarFilterState>)
      return
    }
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    if (key === 'status') {
      setFilter({ status: next, activeOnly: false } as Partial<RadarFilterState>)
      return
    }
    setFilter({ [key]: next } as Partial<RadarFilterState>)
  }

  const statusLabel = (bucket: string) => {
    switch (bucket) {
      case 'pending': return ft.statusPending
      case 'interested': return ft.statusInterested
      case 'filled': return ft.statusFilled
      case 'declined': return ft.statusDeclined
      case 'cancelled': return ft.statusCancelled
      case 'expired': return ft.statusExpired
      case 'assigned_elsewhere': return ft.statusAssignedElsewhere
      default: return bucket
    }
  }
  const zoneLabel = (zone: string) => {
    if (zone === 'panic') return ft.zonePanic
    if (zone === 'middle') return ft.zoneMiddle
    return ft.zoneOuter
  }
  const typeLabel = (type: string) => (type === 'incoming' ? ft.typeIncoming : ft.typeOutgoing)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/20 backdrop-blur-[2px] z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel: fixed to viewport so it is never clipped by an overflow-hidden
          ancestor (GlassContainer). Mobile = bottom sheet, desktop = right panel. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ft.title}
        tabIndex={-1}
        className="
          fixed inset-x-2 bottom-2 z-50 max-h-[78vh] overflow-y-auto
          md:inset-x-auto md:bottom-auto md:top-20 md:right-4
          md:w-80 md:max-h-[75vh]
          rounded-2xl border border-white/60 bg-white/95 backdrop-blur-xl
          shadow-2xl p-4 outline-none
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3 sticky top-0 bg-white/95 backdrop-blur pb-2 -mt-1 pt-1 z-10">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            {ft.title}
            {isLocked && <Lock className="w-3.5 h-3.5 text-gray-400" />}
          </h4>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={resetFilter}
              disabled={disabled}
              className="flex items-center gap-1 text-[11px] text-blue-600 hover:underline disabled:opacity-40 disabled:no-underline px-1.5 py-1"
              title={ft.reset}
            >
              <RotateCcw className="w-3 h-3" /> {ft.reset}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label={ft.close}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {isLocked && (
          <div className="mb-3 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2 flex items-start gap-1.5">
            <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{ft.lockedHint}</span>
          </div>
        )}

        {/* Meta: activeOnly */}
        <div className={`mb-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filterState.activeOnly}
              onChange={(e) => setFilter({ activeOnly: e.target.checked })}
              className="accent-blue-500"
            />
            {ft.activeOnly}
          </label>
          {filterState.activeOnly && (
            <div className="text-[10px] text-amber-600 mt-1 ml-6">{ft.activeOnlyHint}</div>
          )}
        </div>

        {/* Status — clickable even with activeOnly; a click turns activeOnly off. */}
        <div className={`mb-3 ${filterState.activeOnly ? 'opacity-60' : ''}`}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
            {ft.sectionStatus}
          </div>
          <div className="space-y-0.5">
            {STATUS_BUCKET_META.filter((m) => !m.teacherOnly || !isAdmin).map((m) => {
              const on = filterState.activeOnly
                ? m.bucket === 'pending' || m.bucket === 'interested' || m.bucket === 'filled'
                : filterState.status.includes(m.bucket)
              return (
                <label
                  key={m.bucket}
                  className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-white/60 rounded px-1 py-0.5"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleInList('status', m.bucket)}
                    disabled={disabled}
                    className="accent-blue-500"
                  />
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: m.color }} />
                  <span className="flex-1">{statusLabel(m.bucket)}</span>
                  <span className="text-[10px] text-gray-400">{statusCounts[m.bucket] ?? 0}</span>
                </label>
              )
            })}
          </div>
        </div>

        {/* Zone */}
        <div className={`mb-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
            {ft.sectionZone}
          </div>
          <div className="flex flex-wrap gap-2">
            {ZONE_META.map((m) => {
              const on = filterState.zones.includes(m.zone)
              return (
                <label
                  key={m.zone}
                  className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleInList('zones', m.zone)}
                    disabled={disabled}
                    className="accent-blue-500"
                  />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
                  {zoneLabel(m.zone)} ({zoneCounts[m.zone] ?? 0})
                </label>
              )
            })}
          </div>
        </div>

        {/* Type — only when incoming enabled */}
        {showIncoming && (
          <div className={`mb-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
              {ft.sectionType}
            </div>
            <div className="flex flex-wrap gap-2">
              {TYPE_META.map((m) => {
                const on = filterState.types.includes(m.type)
                return (
                  <label key={m.type} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleInList('types', m.type)}
                      disabled={disabled}
                      className="accent-blue-500"
                    />
                    {typeLabel(m.type)}
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {/* Structural dimensions — inline checklists */}
        <FilterChecklistSection
          title={ft.sectionSubject}
          options={subjectOptions}
          selected={filterState.subjects}
          onChange={(next) => setFilter({ subjects: next })}
          disabled={disabled}
          searchPlaceholder={ft.checklistSearch}
          labelAll={ft.checklistAll}
          labelNoneRestriction={ft.checklistNoneRestriction}
          emptyHint={ft.checklistAllVisible}
          noHitsLabel={ft.checklistNoHits}
        />
        <FilterChecklistSection
          title={ft.sectionFaculty}
          options={facultyOptions}
          selected={filterState.faculties}
          onChange={(next) => setFilter({ faculties: next })}
          disabled={disabled}
          searchPlaceholder={ft.checklistSearch}
          labelAll={ft.checklistAll}
          labelNoneRestriction={ft.checklistNoneRestriction}
          emptyHint={ft.checklistAllVisible}
          noHitsLabel={ft.checklistNoHits}
        />
        <FilterChecklistSection
          title={ft.sectionClass}
          options={classOptions}
          selected={filterState.classes}
          onChange={(next) => setFilter({ classes: next })}
          disabled={disabled}
          searchPlaceholder={ft.checklistSearch}
          labelAll={ft.checklistAll}
          labelNoneRestriction={ft.checklistNoneRestriction}
          emptyHint={ft.checklistAllVisible}
          noHitsLabel={ft.checklistNoHits}
        />
        {isAdmin && (
          <FilterChecklistSection
            title={ft.sectionRequester}
            options={requesterOptions}
            selected={filterState.requesters}
            onChange={(next) => setFilter({ requesters: next })}
            disabled={disabled}
            searchPlaceholder={ft.checklistSearch}
            labelAll={ft.checklistAll}
            labelNoneRestriction={ft.checklistNoneRestriction}
            emptyHint={ft.checklistAllVisible}
            noHitsLabel={ft.checklistNoHits}
          />
        )}

        {/* Specials */}
        <div className={`mb-1 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1.5">
            {ft.sectionSpecials}
          </div>
          <div className="text-[10px] text-gray-500 mb-1">{ft.specialsOrHint}</div>
          <div className="space-y-0.5">
            {([
              ['prep_requested', ft.specialPrepRequested],
              ['prep_open', ft.specialPrepOpen],
              ['director_action_pending', ft.specialDirectorAction],
              ['has_unread', ft.specialHasUnread],
              ['storno_pending', ft.specialStornoPending],
              ['is_split', ft.specialIsSplit],
              ['partial', ft.specialPartial],
              ['needs_admin_assignment', ft.specialNeedsAdminAssignment],
              ['director_gate_pending', ft.specialDirectorGate],
            ] as const).filter(([key]) => (
              directorEnabled
              || (key !== 'director_action_pending' && key !== 'director_gate_pending')
            )).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:bg-white/60 rounded px-1 py-0.5">
                <input
                  type="checkbox"
                  checked={filterState.specials[key]}
                  onChange={(e) => setFilter({ specials: { ...filterState.specials, [key]: e.target.checked } })}
                  disabled={disabled}
                  className="accent-blue-500"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Footer summary (V3-E1) */}
        {typeof totalCount === 'number' && typeof visibleCount === 'number' && (
          <div className="mt-3 pt-2 border-t border-gray-200 text-[11px] text-gray-500">
            {visibleCount === totalCount
              ? ft.visibleAll(totalCount)
              : ft.visiblePartial(visibleCount, totalCount)}
          </div>
        )}
      </div>
    </>
  )
}
